<template>
  <gg-modal :is-active="isActive" width="800" @close="close">
    <template v-slot:toolbar>
      Create Filter
    </template>
    <template v-slot:body>
      <v-row class="pt-2">
        <v-col cols="12" sm="8" class="py-0">
          <!-- A filter belongs to one address, so that comes first. -->
          <gg-select
            v-model="account"
            label="Mailbox"
            :items="accountOptions"
            :loading="loading"
            :disabled="loading || saving"
          />
        </v-col>
      </v-row>
      <!-- Keyed by address so switching starts the rules afresh. -->
      <create-filter
        v-if="isActive && account"
        ref="form"
        :key="account"
        :account="account"
        embedded
        @saving="saving = $event"
        @stored="stored"
      />
    </template>
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <gg-btn secondary @click="close">Cancel</gg-btn>
      <gg-btn primary :loading="saving" :disabled="loading || !account" @click="submit">
        Create Filter
      </gg-btn>
    </template>
  </gg-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CreateFilter from '@/components/Services/Hosting/Shared/Email/GlobalFilters/CreateFilter'

/**
 * The first filter on an account, opened from the Filters empty state
 * (AM-1107). The rules and actions builder is the hosting section's own
 * CreateFilter, embedded so the two never drift apart.
 */
export default {
  name: 'CreateFilterModal',
  components: { CreateFilter },
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    service: {
      type: Object,
      required: true,
    },
    // The domain the account is opened on: the main domain or an addon.
    scopeDomain: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      account: '',
      loading: false,
      saving: false,
    }
  },
  computed: {
    ...mapGetters('emailHub', ['scopedMailboxes']),
    isMainDomain () {
      return String(this.service.domain).toLowerCase() === String(this.scopeDomain).toLowerCase()
    },
    /**
     * The opened domain's mailboxes, and on the main domain also the cPanel
     * user's own default mailbox — the same addresses the Filters list shows.
     */
    accountOptions () {
      const options = this.scopedMailboxes(this.service.id, this.scopeDomain)
        .map(mailbox => mailbox.email)
        .sort()
        .map(email => ({ text: email, value: email }))
      if (this.isMainDomain && this.service.username) {
        options.push({ text: `${this.service.username} (default mailbox)`, value: this.service.username })
      }
      return options
    },
  },
  methods: {
    ...mapActions('emailHub', ['loadServiceMailboxes']),
    close () {
      this.$emit('close')
    },
    async open () {
      this.account = ''
      this.saving = false
      this.loading = true
      try {
        await this.loadServiceMailboxes(this.service)
      } catch (e) {
        console.warn('CreateFilterModal: unable to load mailboxes', e)
      } finally {
        this.loading = false
      }
      const first = this.accountOptions[0]
      if (first) this.account = first.value
    },
    submit () {
      if (this.$refs.form) this.$refs.form.addRule()
    },
    stored (account) {
      this.$emit('created', account)
      this.close()
    },
  },
  watch: {
    isActive (active) {
      if (active) this.open()
    },
  },
  created () {
    if (this.isActive) this.open()
  },
}
</script>
